import { useEffect, useState } from "react";
import { peerCompare, setResearchConsent, setDemographics } from "../api.js";
import { compareLine, signed } from "../compare.js";
import { AGE_BANDS } from "../demographics.js";
import Choice from "./Choice.jsx";

/**
 * 「다른 분들은 어땠나」 — 연구·통계 동의의 대가.
 *
 * 동의율을 올리는 정당한 방법은 체크박스를 미리 켜 두는 게 아니라
 * **동의할 이유를 만드는 것**이다. 사전 체크된 선택 동의는 유효한 동의로
 * 보지 않고, 무효가 되면 그 동의로 모은 데이터가 통째로 못 쓰게 된다.
 *
 * 평균은 서버가 낸다 — 표본이 적은 칸을 감추는 판단이 화면에 있으면
 * 화면을 고치는 것만으로 뚫린다.
 */
export default function PeerCompare({ debate }) {
  const [data, setData] = useState(null); // null = 확인 중
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [age, setAge] = useState(null);

  const load = () =>
    peerCompare(debate.id)
      .then(setData)
      // 부가 기능이다. 실패해도 결과 화면을 깨뜨리지 않는다.
      .catch(() => setData({ consented: false, all: null, sameAge: null }));

  useEffect(() => { load(); }, [debate.id]);

  async function agree() {
    setBusy(true);
    setErr(null);
    try {
      await setResearchConsent(true);
      await load();
    } catch (e) {
      setErr(e?.message ?? "지금은 안 되네요. 잠시 뒤에 다시 해주세요.");
    } finally {
      setBusy(false);
    }
  }

  async function saveAge(band) {
    setAge(band);
    if (!band) return;
    setBusy(true);
    setErr(null);
    try {
      await setDemographics({ ageBand: band, job: null, gender: null });
      await load();
    } catch (e) {
      setErr(e?.message ?? "저장이 안 됐어요.");
    } finally {
      setBusy(false);
    }
  }

  if (!data) return null;

  if (!data.consented) {
    return (
      <section className="card ai" style={{ borderRadius: 4, gap: 10 }}>
        <h2 className="label">다른 분들은 어땠을까요</h2>
        {/* peerCompareFn 이 동의한 사람의 토론만 세므로, 이 문장은 사실이다. */}
        <p style={{ fontSize: 15, fontWeight: 700, lineHeight: 1.6 }}>
          같은 주제를 끝까지 간 분들의 확신도가 평균 얼마나 움직였는지 보여드려요.
          <strong> 이 숫자는 동의하신 분들이 만든 거예요.</strong> 같이 만드실래요?
        </p>
        <p className="note" style={{ margin: 0 }}>
          개인을 알아볼 수 없는 통계로만 씁니다. 제 발언 원문이 그대로 팔리지는 않아요. 언제든 그만둘 수 있어요.{" "}
          <a href="https://sowhat.run/privacy" target="_blank" rel="noreferrer" style={{ textDecoration: "underline" }}>자세히</a>
        </p>
        {err && <p className="note" role="alert" style={{ margin: 0 }}>{err}</p>}
        <button type="button" className="btn" style={{ alignSelf: "flex-start", minHeight: 44 }}
          disabled={busy} onClick={agree}>
          {busy ? "여는 중…" : "동의하고 보기"}
        </button>
      </section>
    );
  }

  const { mine, all, sameAge, ageBand, minPeers } = data;

  if (!all) {
    return (
      <section className="card dashed" style={{ borderRadius: 4, gap: 6 }}>
        <h2 className="label">다른 분들은 어땠을까요</h2>
        <p style={{ fontSize: 14, fontWeight: 700, lineHeight: 1.6 }}>
          이 주제는 아직 {minPeers}명이 안 됐어요. 조금 더 쌓이면 여기에 보여드릴게요.
        </p>
      </section>
    );
  }

  return (
    <section className="card mark raised" style={{ borderRadius: 4, gap: 12 }}>
      <h2 className="label">다른 분들은 어땠을까요</h2>
      <p className="d" style={{ fontSize: 22, lineHeight: 1.3 }}>{compareLine(mine, all)}</p>

      <div style={{ display: "grid", gridTemplateColumns: `repeat(${sameAge ? 3 : 2}, minmax(0, 1fr))`, gap: 10 }}>
        <Cell label="나" value={signed(mine.delta)} strong />
        <Cell label={`다른 ${all.peers}명`} value={signed(all.delta)} />
        {sameAge && <Cell label={`${ageBand} ${sameAge.peers}명`} value={signed(sameAge.delta)} />}
      </div>

      <p className="note" style={{ margin: 0 }}>
        답하지 않은 질문은 나 {mine.evasions}개, 다른 분들 평균 {all.evasions.toFixed(1)}개예요.
      </p>

      {/* 또래 비교가 연령대를 넣을 이유다. 안 넣은 사람에게만, 여기서 바로 받는다. */}
      {!ageBand && (
        <div style={{ display: "flex", flexDirection: "column", gap: 8, paddingTop: 4 }}>
          <span className="note" style={{ fontWeight: 800, color: "var(--ink)" }}>
            연령대를 넣으시면 또래와도 비교해드려요 <span style={{ color: "var(--muted)" }}>(선택)</span>
          </span>
          <Choice label="연령대" options={AGE_BANDS} value={age} onChange={saveAge} />
          {busy && <span className="note">저장하는 중…</span>}
          {err && <span className="note" role="alert">{err}</span>}
        </div>
      )}

      <p className="note" style={{ margin: 0 }}>
        확신도가 얼마나 움직였는지예요. 덜 움직인 게 더 나은 건 아니에요 —
        답하지 않은 채로 버틴 것일 수도 있으니까요.
      </p>
    </section>
  );
}

function Cell({ label, value, strong = false }) {
  return (
    <div style={{
      padding: "14px 12px", border: "1px solid var(--line)", borderRadius: 4,
      background: strong ? "var(--ink)" : "var(--paper)",
      color: strong ? "var(--paper)" : "var(--ink)",
      display: "flex", flexDirection: "column", gap: 2, minWidth: 0,
    }}>
      <span className="label" style={{
        color: strong ? "var(--red)" : undefined,
        whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
      }}>{label}</span>
      <span className="num" style={{ fontSize: 24, fontWeight: 800 }}>{value}</span>
    </div>
  );
}
